<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLeadsStore } from 'dashboard/stores/leads';
import LeadsAPI from 'dashboard/api/leads';
import { useLeadActivitiesStore } from 'dashboard/stores/leadActivities';
import { useFollowUpTemplatesStore } from 'dashboard/stores/followUpTemplates';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const emit = defineEmits(['lead-updated']);
const { t } = useI18n();
const store = useStore();
const leadsStore = useLeadsStore();
const activitiesStore = useLeadActivitiesStore();
const templatesStore = useFollowUpTemplatesStore();

const agents = useMapGetter('agents/getAgents');

const dialogRef = ref(null);
const tab = ref('summary');
const lead = ref(null);
const loading = ref(false);

// Histórico de mudanças (trilha de auditoria do lead) - carregado sob demanda
// ao abrir a aba, para não pesar a abertura do drawer.
const history = ref([]);
const historyLoading = ref(false);
const historyError = ref(false);

async function fetchHistory() {
  if (!lead.value) return;
  historyLoading.value = true;
  historyError.value = false;
  try {
    const { data } = await LeadsAPI.history(lead.value.id);
    history.value = Array.isArray(data) ? data : [];
  } catch (e) {
    historyError.value = true;
  } finally {
    historyLoading.value = false;
  }
}

const historyTime = iso => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch (e) {
    return '';
  }
};

const ACTION_LABELS = {
  create: 'PIPELINES.LEAD_DRAWER.HISTORY_ACTIONS.CREATE',
  update: 'PIPELINES.LEAD_DRAWER.HISTORY_ACTIONS.UPDATE',
  destroy: 'PIPELINES.LEAD_DRAWER.HISTORY_ACTIONS.DESTROY',
};
const actionLabel = a => (ACTION_LABELS[a] ? t(ACTION_LABELS[a]) : a);

watch(tab, value => {
  if (value === 'history' && !history.value.length && !historyLoading.value) {
    fetchHistory();
  }
});
const assigningOwner = ref(false);
const ownerId = ref('');

// Opções no formato do Select padrão da plataforma ({ value, label }).
const ownerOptions = computed(() => [
  { value: '', label: t('PIPELINES.LEAD_DRAWER.OWNER.NONE') },
  ...(agents.value || []).map(a => ({ value: a.id, label: a.name })),
]);

// Rascunho editável do resumo. A IA do CRM já preenche `lead.summary` no
// atendimento (tool crm_create_lead); aqui o humano edita/sobrepõe.
const summaryDraft = ref('');
const savingSummary = ref(false);
const generatingSummary = ref(false);
const summaryDirty = computed(
  () => (summaryDraft.value || '') !== (lead.value?.summary || '')
);

// Dialog state para substituir prompt() - input reusável via slot
const applyTemplateDialogRef = ref(null);
const applyTemplateId = ref(null);
const applyTemplateReason = ref('');

const discardDialogRef = ref(null);
const discardReason = ref('');

const reopenDialogRef = ref(null);
const reopenReason = ref('');

const TABS = computed(() => [
  { key: 'summary',      label: t('PIPELINES.LEAD_DRAWER.TABS.SUMMARY'),      icon: 'i-lucide-info' },
  { key: 'activities',   label: t('PIPELINES.LEAD_DRAWER.TABS.ACTIVITIES'),   icon: 'i-lucide-list-checks' },
  { key: 'follow_up',    label: t('PIPELINES.LEAD_DRAWER.TABS.FOLLOW_UP'),    icon: 'i-lucide-route' },
  { key: 'conversation', label: t('PIPELINES.LEAD_DRAWER.TABS.CONVERSATION'), icon: 'i-lucide-message-circle' },
  { key: 'history',      label: t('PIPELINES.LEAD_DRAWER.TABS.HISTORY'),      icon: 'i-lucide-history' },
]);

const CLASS_META = computed(() => ({
  hot:  { label: t('PIPELINES.LEAD_DRAWER.CLASSIFICATION.HOT'),  cls: 'bg-n-ruby-3 text-n-ruby-11' },
  warm: { label: t('PIPELINES.LEAD_DRAWER.CLASSIFICATION.WARM'), cls: 'bg-n-amber-3 text-n-amber-11' },
  cold: { label: t('PIPELINES.LEAD_DRAWER.CLASSIFICATION.COLD'), cls: 'bg-n-blue-3 text-n-blue-11' },
}));

const STATUS_META = computed(() => ({
  draft:       { label: t('PIPELINES.LEAD_DRAWER.STATUS.DRAFT'),       cls: 'bg-n-alpha-2 text-n-slate-11' },
  qualified:   { label: t('PIPELINES.LEAD_DRAWER.STATUS.QUALIFIED'),   cls: 'bg-n-brand/10 text-n-brand' },
  in_pipeline: { label: t('PIPELINES.LEAD_DRAWER.STATUS.IN_PIPELINE'), cls: 'bg-n-blue-3 text-n-blue-11' },
  won:         { label: t('PIPELINES.LEAD_DRAWER.STATUS.WON'),         cls: 'bg-n-grass-3 text-n-grass-11' },
  lost:        { label: t('PIPELINES.LEAD_DRAWER.STATUS.LOST'),        cls: 'bg-n-ruby-3 text-n-ruby-11' },
  discarded:   { label: t('PIPELINES.LEAD_DRAWER.STATUS.DISCARDED'),   cls: 'bg-n-alpha-2 text-n-slate-11' },
}));

async function open(leadId) {
  loading.value = true;
  tab.value = 'summary';
  history.value = [];
  // Garante a lista de agentes carregada para o seletor de responsável.
  store.dispatch('agents/get');
  try {
    lead.value = await leadsStore.fetchOne(leadId);
    summaryDraft.value = lead.value?.summary || '';
    ownerId.value = lead.value?.owner?.id || '';
    await activitiesStore.fetchForLead(leadId);
    await templatesStore.fetchAll({ pipeline_id: lead.value.pipeline?.id });
  } finally {
    loading.value = false;
  }
  dialogRef.value?.open();
}

// Atribui (ou remove) o responsável pelo lead manualmente (via Select padrão).
async function assignOwner(value) {
  if (!lead.value || assigningOwner.value) return;
  const newOwnerId = value ? Number(value) : null;
  if (newOwnerId === (lead.value.owner?.id || null)) return;
  assigningOwner.value = true;
  try {
    lead.value = await leadsStore.update(lead.value.id, {
      owner_id: newOwnerId,
    });
    ownerId.value = lead.value?.owner?.id || '';
    useAlert(t('PIPELINES.LEAD_DRAWER.ALERTS.OWNER_UPDATED'));
    emit('lead-updated');
  } catch (e) {
    ownerId.value = lead.value?.owner?.id || '';
    useAlert(
      e?.response?.data?.error || t('PIPELINES.LEAD_DRAWER.ALERTS.OWNER_FAILED')
    );
  } finally {
    assigningOwner.value = false;
  }
}
function close() { dialogRef.value?.close(); }
defineExpose({ open, close });

// Qualifica e roteia pro pipeline direto do drawer - evita o lead ficar
// preso em 'draft' invisível no Kanban. Usa o pipeline já vinculado ao
// lead; senão o backend resolve via intent (PipelineRoutingService).
async function qualifyLead() {
  if (!lead.value) return;
  try {
    const payload = lead.value.pipeline?.id
      ? { pipeline_id: lead.value.pipeline.id }
      : {};
    lead.value = await leadsStore.qualify(lead.value.id, payload);
    useAlert(t('PIPELINES.LEAD_DRAWER.ALERTS.QUALIFIED'));
    emit('lead-updated');
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        t('PIPELINES.LEAD_DRAWER.ALERTS.QUALIFY_FAILED')
    );
  }
}

// Gera o resumo via IA a partir da conversa do lead. NÃO salva - preenche o
// rascunho pro agente revisar e clicar "Salvar resumo" (humano sobre IA).
async function generateSummaryAi() {
  if (!lead.value || generatingSummary.value) return;
  generatingSummary.value = true;
  try {
    const summary = await leadsStore.generateSummary(lead.value.id);
    if (summary) {
      summaryDraft.value = summary;
      useAlert(t('PIPELINES.LEAD_DRAWER.ALERTS.SUMMARY_GENERATED'));
    } else {
      useAlert(t('PIPELINES.LEAD_DRAWER.ALERTS.SUMMARY_EMPTY'));
    }
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        t('PIPELINES.LEAD_DRAWER.ALERTS.SUMMARY_GENERATE_FAILED')
    );
  } finally {
    generatingSummary.value = false;
  }
}

async function saveSummary() {
  if (!lead.value || !summaryDirty.value || savingSummary.value) return;
  savingSummary.value = true;
  try {
    lead.value = await leadsStore.update(lead.value.id, {
      summary: summaryDraft.value,
    });
    summaryDraft.value = lead.value?.summary || '';
    useAlert(t('PIPELINES.LEAD_DRAWER.ALERTS.SUMMARY_UPDATED'));
    emit('lead-updated');
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        t('PIPELINES.LEAD_DRAWER.ALERTS.SUMMARY_SAVE_FAILED')
    );
  } finally {
    savingSummary.value = false;
  }
}

const activities = computed(() => activitiesStore.byLead[lead.value?.id] || []);
const followUpInfo = computed(() => lead.value?.active_follow_up);
const classMeta = computed(() => CLASS_META.value[lead.value?.classification] || CLASS_META.value.warm);
const statusMeta = computed(() => STATUS_META.value[lead.value?.status] || STATUS_META.value.draft);
const valueFormatted = computed(() => {
  if (!lead.value?.value_cents) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: lead.value?.currency || 'BRL',
  }).format(lead.value.value_cents / 100);
});

function applyTemplate(templateId) {
  applyTemplateId.value = templateId;
  applyTemplateReason.value = '';
  applyTemplateDialogRef.value?.open();
}

async function confirmApplyTemplate() {
  const templateId = applyTemplateId.value;
  if (!templateId) return;
  const reason =
    applyTemplateReason.value.trim() ||
    t('PIPELINES.LEAD_DRAWER.ALERTS.DEFAULT_TEMPLATE_REASON');
  try {
    await leadsStore.applyFollowUp(lead.value.id, templateId, reason);
    await open(lead.value.id);
    emit('lead-updated');
    useAlert(t('PIPELINES.LEAD_DRAWER.ALERTS.TEMPLATE_APPLIED'));
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        t('PIPELINES.LEAD_DRAWER.ALERTS.TEMPLATE_APPLY_FAILED')
    );
  } finally {
    applyTemplateDialogRef.value?.close();
    applyTemplateId.value = null;
    applyTemplateReason.value = '';
  }
}

async function completeStep(stepId) {
  try {
    await activitiesStore.complete(lead.value.id, stepId, 'success', null);
    await open(lead.value.id);
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        t('PIPELINES.LEAD_DRAWER.ALERTS.STEP_COMPLETE_FAILED')
    );
  }
}

function discard() {
  discardReason.value = '';
  discardDialogRef.value?.open();
}

async function confirmDiscard() {
  const reason = discardReason.value.trim();
  if (!reason) return;
  try {
    await leadsStore.discard(lead.value.id, reason);
    emit('lead-updated');
    discardDialogRef.value?.close();
    discardReason.value = '';
    close();
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        t('PIPELINES.LEAD_DRAWER.ALERTS.DISCARD_FAILED')
    );
  }
}

function reopen() {
  reopenReason.value = '';
  reopenDialogRef.value?.open();
}

async function confirmReopen() {
  const reason = reopenReason.value.trim();
  if (!reason) return;
  try {
    await leadsStore.reopen(lead.value.id, reason);
    emit('lead-updated');
    reopenDialogRef.value?.close();
    reopenReason.value = '';
  } catch (e) {
    useAlert(
      e?.response?.data?.error || t('PIPELINES.LEAD_DRAWER.ALERTS.REOPEN_FAILED')
    );
  }
}
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    position="top"
    overflow-y-auto
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div v-if="lead" class="flex flex-col gap-4">
      <!-- Header -->
      <header class="flex items-start justify-between gap-3">
        <div class="flex items-start gap-3 min-w-0">
          <span class="size-10 rounded-full bg-n-brand/10 flex items-center justify-center flex-shrink-0">
            <i class="i-lucide-target size-5 text-n-brand" />
          </span>
          <div class="min-w-0">
            <h3 class="text-heading-3 text-n-slate-12 truncate mb-0">
              {{ lead.contact?.name || $t('PIPELINES.LEAD_DRAWER.FALLBACK_NAME') }}
            </h3>
            <div class="flex items-center gap-2 mt-1 flex-wrap">
              <span class="text-label-small px-1.5 py-0.5 rounded uppercase font-medium" :class="statusMeta.cls">
                {{ statusMeta.label }}
              </span>
              <span class="text-label-small px-1.5 py-0.5 rounded uppercase font-medium" :class="classMeta.cls">
                {{ classMeta.label }}
              </span>
              <span v-if="lead.pipeline?.name" class="text-body-small text-n-slate-11 inline-flex items-center gap-1">
                <i class="i-lucide-kanban size-3" />{{ lead.pipeline.name }}
              </span>
              <span v-if="lead.stage?.name" class="text-body-small text-n-slate-11 inline-flex items-center gap-1">
                ·
                <i class="i-lucide-layers size-3" />{{ lead.stage.name }}
              </span>
              <span v-if="valueFormatted" class="text-body-small font-semibold text-n-slate-12 inline-flex items-center gap-1">
                ·
                <i class="i-lucide-banknote size-3" />{{ valueFormatted }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <NextButton
            v-if="['draft','qualified'].includes(lead.status)"
            color="teal" size="sm"
            icon="i-lucide-circle-check-big"
            :label="$t('PIPELINES.LEAD_DRAWER.ACTIONS.QUALIFY')"
            @click="qualifyLead"
          />
          <NextButton
            v-if="['won','lost','discarded'].includes(lead.status)"
            faded slate size="sm"
            icon="i-lucide-rotate-ccw"
            :label="$t('PIPELINES.LEAD_DRAWER.ACTIONS.REOPEN')"
            @click="reopen"
          />
          <NextButton
            v-else
            faded ruby size="sm"
            icon="i-lucide-archive-x"
            :label="$t('PIPELINES.LEAD_DRAWER.ACTIONS.DISCARD')"
            @click="discard"
          />
          <button
            class="text-n-slate-11 hover:text-n-slate-12 p-1.5 rounded hover:bg-n-alpha-2"
            :aria-label="$t('PIPELINES.LEAD_DRAWER.ACTIONS.CLOSE')"
            @click="close"
          >
            <i class="i-lucide-x size-4" />
          </button>
        </div>
      </header>

      <!-- Tabs -->
      <nav class="flex gap-1 border-b border-n-weak -mx-6 px-6">
        <button
          v-for="t in TABS"
          :key="t.key"
          type="button"
          class="flex items-center gap-1.5 px-3 py-2 text-body-main border-b-2 transition"
          :class="tab === t.key
            ? 'border-n-brand text-n-slate-12 font-medium'
            : 'border-transparent text-n-slate-11 hover:text-n-slate-12'"
          @click="tab = t.key"
        >
          <i :class="t.icon" class="size-3.5" />
          {{ t.label }}
        </button>
      </nav>

      <!-- Resumo -->
      <section v-if="tab === 'summary'" class="rounded-xl border border-n-weak bg-n-solid-1">
        <div class="px-5 py-4 grid grid-cols-2 gap-x-6 gap-y-3 text-body-main">
          <div>
            <div class="mb-1 text-body-small text-n-slate-11">{{ $t('PIPELINES.LEAD_DRAWER.OWNER.LABEL') }}</div>
            <Select
              v-model="ownerId"
              :options="ownerOptions"
              :disabled="assigningOwner"
              @update:model-value="assignOwner"
            />
          </div>
          <div>
            <div class="text-body-small text-n-slate-11">{{ $t('PIPELINES.LEAD_DRAWER.FIELDS.INTENT') }}</div>
            <div class="text-n-slate-12">{{ lead.lead_intent_name || '-' }}</div>
          </div>
          <div>
            <div class="text-body-small text-n-slate-11">{{ $t('PIPELINES.LEAD_DRAWER.FIELDS.SOURCE') }}</div>
            <div class="text-n-slate-12">{{ lead.source || '-' }}</div>
          </div>
          <div v-if="lead.expected_close_date">
            <div class="text-body-small text-n-slate-11">{{ $t('PIPELINES.LEAD_DRAWER.FIELDS.EXPECTED_CLOSE_DATE') }}</div>
            <div class="text-n-slate-12">{{ lead.expected_close_date }}</div>
          </div>
          <div v-if="lead.lost_reason_name" class="col-span-2">
            <div class="text-body-small text-n-slate-11">{{ $t('PIPELINES.LEAD_DRAWER.FIELDS.LOST_REASON') }}</div>
            <div class="text-n-ruby-11">{{ lead.lost_reason_name }}</div>
          </div>
          <div class="col-span-2">
            <div class="flex items-center justify-between mb-1.5">
              <div class="text-body-small text-n-slate-11">{{ $t('PIPELINES.LEAD_DRAWER.SUMMARY.LABEL') }}</div>
              <div class="flex items-center gap-3">
                <span
                  v-if="lead.ai_qualified && !summaryDirty"
                  class="text-label-small text-n-brand inline-flex items-center gap-1"
                >
                  <i class="i-lucide-sparkles size-3" />
                  {{ $t('PIPELINES.LEAD_DRAWER.SUMMARY.AI_GENERATED') }}
                </span>
                <NextButton
                  v-if="lead.ai_summary_available"
                  ghost
                  size="sm"
                  icon="i-lucide-sparkles"
                  :label="$t('PIPELINES.LEAD_DRAWER.SUMMARY.GENERATE_WITH_AI')"
                  :is-loading="generatingSummary"
                  @click="generateSummaryAi"
                />
              </div>
            </div>
            <textarea
              v-model="summaryDraft"
              rows="4"
              :placeholder="$t('PIPELINES.LEAD_DRAWER.SUMMARY.PLACEHOLDER')"
              class="w-full rounded-lg border-none outline outline-1 outline-offset-[-1px] bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand transition-all duration-200 px-3 py-2.5 text-sm resize-y !mb-0"
            />
            <div v-if="summaryDirty" class="flex justify-end gap-2 mt-2">
              <NextButton
                faded slate size="sm"
                :label="$t('PIPELINES.LEAD_DRAWER.SUMMARY.CANCEL')"
                @click="summaryDraft = lead.summary || ''"
              />
              <NextButton
                size="sm"
                :label="$t('PIPELINES.LEAD_DRAWER.SUMMARY.SAVE')"
                :is-loading="savingSummary"
                @click="saveSummary"
              />
            </div>
          </div>
        </div>
        <footer v-if="lead.ai_qualified" class="px-5 py-2.5 border-t border-n-weak bg-n-brand/5 text-body-small text-n-brand flex items-center gap-2">
          <i class="i-lucide-sparkles size-3.5" />
          {{ $t('PIPELINES.LEAD_DRAWER.SUMMARY.AI_QUALIFIED', { confidence: Number(lead.ai_confidence).toFixed(2) }) }}
        </footer>
      </section>

      <!-- Atividades -->
      <section v-if="tab === 'activities'" class="rounded-xl border border-n-weak bg-n-solid-1">
        <header class="px-5 py-3 border-b border-n-weak flex items-center justify-between">
          <h4 class="text-heading-3 text-n-slate-12 mb-0">{{ $t('PIPELINES.LEAD_DRAWER.ACTIVITIES.TITLE') }}</h4>
          <span class="text-body-small text-n-slate-11">{{ $t('PIPELINES.LEAD_DRAWER.ACTIVITIES.COUNT', { count: activities.length }) }}</span>
        </header>
        <div v-if="!activities.length" class="px-5 py-10 text-center">
          <i class="i-lucide-clipboard-list size-8 text-n-slate-10 mx-auto block mb-2" />
          <p class="text-body-small text-n-slate-11 mb-0">{{ $t('PIPELINES.LEAD_DRAWER.ACTIVITIES.EMPTY') }}</p>
        </div>
        <ol v-else class="flex flex-col">
          <li
            v-for="a in activities"
            :key="a.id"
            class="px-5 py-3 border-b border-n-weak last:border-b-0 flex items-center gap-3"
          >
            <span
              class="size-8 rounded-full flex items-center justify-center flex-shrink-0"
              :class="a.status === 'completed' ? 'bg-n-grass-3' : 'bg-n-alpha-2'"
            >
              <i
                class="size-4"
                :class="a.status === 'completed' ? 'i-lucide-check text-n-grass-11' : 'i-lucide-clock text-n-slate-12'"
              />
            </span>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="text-body-main font-medium text-n-slate-12">{{ a.title }}</span>
                <span class="text-label-small px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11 uppercase tracking-wide">
                  {{ a.kind }}
                </span>
              </div>
              <div class="text-body-small text-n-slate-11">{{ a.due_at }}</div>
              <p v-if="a.outcome_notes" class="text-body-small text-n-slate-10 italic mt-0.5 mb-0">{{ a.outcome_notes }}</p>
            </div>
            <NextButton
              v-if="a.status === 'pending'"
              faded slate size="sm"
              icon="i-lucide-check"
              :label="$t('PIPELINES.LEAD_DRAWER.ACTIVITIES.COMPLETE')"
              @click="completeStep(a.id)"
            />
          </li>
        </ol>
      </section>

      <!-- Follow-up -->
      <section v-if="tab === 'follow_up'" class="flex flex-col gap-3">
        <div
          v-if="followUpInfo"
          class="rounded-xl border border-n-weak bg-n-solid-1 px-5 py-4 flex items-center gap-3"
        >
          <span class="size-10 rounded-full bg-n-brand/10 flex items-center justify-center flex-shrink-0">
            <i class="i-lucide-route size-5 text-n-brand" />
          </span>
          <div class="flex-1 min-w-0">
            <p class="text-body-main font-medium text-n-slate-12 mb-0 flex items-center gap-2">
              {{ followUpInfo.template_name }}
              <span class="text-label-small px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11 uppercase tracking-wide">
                v{{ followUpInfo.template_version }}
              </span>
            </p>
            <p class="text-body-small text-n-slate-11 mb-0">{{ $t('PIPELINES.LEAD_DRAWER.FOLLOW_UP.STATUS_ANCHOR', { status: followUpInfo.status, anchor: followUpInfo.anchor_at }) }}</p>
          </div>
        </div>
        <div v-else class="rounded-xl border border-dashed border-n-weak px-5 py-6 text-center text-body-small text-n-slate-11">
          {{ $t('PIPELINES.LEAD_DRAWER.FOLLOW_UP.NO_ACTIVE') }}
        </div>

        <div class="rounded-xl border border-n-weak bg-n-solid-1">
          <header class="px-5 py-3 border-b border-n-weak">
            <h4 class="text-heading-3 text-n-slate-12 mb-0.5">{{ $t('PIPELINES.LEAD_DRAWER.FOLLOW_UP.APPLY_TITLE') }}</h4>
            <p class="text-body-small text-n-slate-11 mb-0">
              {{ $t('PIPELINES.LEAD_DRAWER.FOLLOW_UP.APPLY_HELP') }}
            </p>
          </header>
          <div class="px-5 py-4 flex flex-wrap gap-2">
            <button
              v-for="t in templatesStore.list"
              :key="t.id"
              class="text-body-small px-3 py-1.5 rounded-lg border border-n-weak hover:border-n-brand transition-colors"
              @click="applyTemplate(t.id)"
            >
              {{ t.name }}
            </button>
            <span v-if="!templatesStore.list.length" class="text-body-small text-n-slate-11 italic">
              {{ $t('PIPELINES.LEAD_DRAWER.FOLLOW_UP.EMPTY') }}
            </span>
          </div>
        </div>
      </section>

      <!-- Conversas - Fase 1.5 suporta múltiplas. A primary é a conversa-mãe;
           secundárias são re-aberturas do mesmo contato vinculadas pelo
           LeadConversationLinkListener. -->
      <section v-if="tab === 'conversation'" class="rounded-xl border border-n-weak bg-n-solid-1 px-5 py-4 text-body-main flex flex-col gap-2">
        <template v-if="lead.conversations && lead.conversations.length">
          <a
            v-for="conv in lead.conversations"
            :key="conv.id"
            :href="`/app/accounts/${lead.account_id}/conversations/${conv.id}`"
            class="rounded-lg border border-n-weak hover:border-n-strong px-3 py-2 flex items-center justify-between gap-3 transition-colors"
          >
            <div class="flex items-center gap-2 min-w-0">
              <i class="i-lucide-message-square size-4 text-n-slate-11" />
              <div class="min-w-0">
                <p class="text-body-main font-medium text-n-slate-12 mb-0 truncate">
                  #{{ conv.display_id || conv.id }}
                  <span v-if="conv.inbox_name" class="text-body-small text-n-slate-11 font-normal">
                    · {{ conv.inbox_name }}
                  </span>
                </p>
                <p class="text-label-small text-n-slate-10 mb-0">
                  <span
                    class="px-1.5 py-0.5 rounded text-label-small uppercase tracking-wide"
                    :class="conv.role === 'primary' ? 'bg-n-brand/10 text-n-brand' : 'bg-n-alpha-2 text-n-slate-11'"
                  >{{ conv.role === 'primary' ? $t('PIPELINES.LEAD_DRAWER.CONVERSATION.ROLE_PRIMARY') : $t('PIPELINES.LEAD_DRAWER.CONVERSATION.ROLE_SECONDARY') }}</span>
                  <span class="ml-1.5">· {{ conv.status }}</span>
                </p>
              </div>
            </div>
            <i class="i-lucide-arrow-up-right size-4 text-n-slate-10" />
          </a>
        </template>

        <!-- Fallback para registros antigos: usa conversation_id direto -->
        <a
          v-else-if="lead.conversation_id"
          :href="`/app/accounts/${lead.account_id}/conversations/${lead.conversation_id}`"
          class="inline-flex items-center gap-2 text-n-brand hover:underline"
        >
          <i class="i-lucide-external-link size-4" />
          {{ $t('PIPELINES.LEAD_DRAWER.CONVERSATION.OPEN_CONVERSATION', { id: lead.conversation_id }) }}
        </a>

        <span v-else class="text-n-slate-11">{{ $t('PIPELINES.LEAD_DRAWER.CONVERSATION.EMPTY') }}</span>
      </section>

      <!-- Histórico (trilha de auditoria do lead) -->
      <section v-if="tab === 'history'" class="rounded-xl border border-n-weak bg-n-solid-1 px-5 py-5">
        <div v-if="historyLoading" class="flex justify-center py-8 text-n-slate-10">
          <span class="i-lucide-loader-circle size-5 animate-spin" />
        </div>
        <div v-else-if="historyError" class="py-8 text-center text-body-small text-n-ruby-11">
          {{ $t('PIPELINES.LEAD_DRAWER.HISTORY.ERROR') }}
        </div>
        <div v-else-if="!history.length" class="flex flex-col items-center gap-1 py-8 text-center">
          <span class="i-lucide-history size-6 text-n-slate-9" />
          <span class="text-body-small text-n-slate-10">{{ $t('PIPELINES.LEAD_DRAWER.HISTORY.EMPTY') }}</span>
        </div>
        <ol v-else class="flex flex-col m-0 list-none">
          <li v-for="(h, i) in history" :key="h.id" class="flex gap-3">
            <div class="flex flex-col items-center">
              <span class="flex justify-center items-center rounded-full size-7 shrink-0 bg-n-iris-3 text-n-iris-11">
                <span class="i-lucide-history size-3.5" />
              </span>
              <span v-if="i < history.length - 1" class="flex-1 my-1 w-px bg-n-weak" />
            </div>
            <div class="flex-1 min-w-0 pb-4">
              <div class="flex flex-wrap gap-2 items-center">
                <span class="text-body-main font-medium text-n-slate-12">{{ actionLabel(h.action) }}</span>
                <span class="text-label-small text-n-slate-10">{{ historyTime(h.created_at) }}</span>
                <span v-if="h.user_name" class="text-label-small text-n-slate-11">· {{ h.user_name }}</span>
              </div>
              <ul v-if="h.changes && Object.keys(h.changes).length" class="flex flex-col gap-1 m-0 mt-1.5 list-none">
                <li v-for="(val, field) in h.changes" :key="field" class="text-body-small text-n-slate-11">
                  <span class="font-medium text-n-slate-12">{{ field }}:</span>
                  <template v-if="Array.isArray(val)">
                    <span class="line-through text-n-slate-10">{{ val[0] ?? '-' }}</span>
                    <span class="mx-1">→</span>
                    <span class="text-n-slate-12">{{ val[1] ?? '-' }}</span>
                  </template>
                  <template v-else>{{ val }}</template>
                </li>
              </ul>
              <p v-else-if="h.comment" class="mt-1 text-body-small text-n-slate-11">{{ h.comment }}</p>
            </div>
          </li>
        </ol>
      </section>
    </div>
    <!-- Loading padrão da plataforma enquanto o fetch do lead resolve -->
    <div v-else class="flex items-center justify-center py-10">
      <Spinner />
    </div>
  </Dialog>

  <!-- Aplicar modelo de follow-up: substitui prompt() -->
  <Dialog
    ref="applyTemplateDialogRef"
    width="md"
    :title="$t('PIPELINES.LEAD_DRAWER.APPLY_TEMPLATE_DIALOG.TITLE')"
    :description="$t('PIPELINES.LEAD_DRAWER.APPLY_TEMPLATE_DIALOG.DESCRIPTION')"
    :confirm-button-label="$t('PIPELINES.LEAD_DRAWER.APPLY_TEMPLATE_DIALOG.CONFIRM')"
    :cancel-button-label="$t('PIPELINES.LEAD_DRAWER.APPLY_TEMPLATE_DIALOG.CANCEL')"
    @confirm="confirmApplyTemplate"
  >
    <div class="flex flex-col gap-2">
      <label class="text-body-small text-n-slate-11">{{ $t('PIPELINES.LEAD_DRAWER.APPLY_TEMPLATE_DIALOG.REASON_LABEL') }}</label>
      <input
        v-model="applyTemplateReason"
        type="text"
        :placeholder="$t('PIPELINES.LEAD_DRAWER.APPLY_TEMPLATE_DIALOG.REASON_PLACEHOLDER')"
        class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-body-main text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
      />
    </div>
  </Dialog>

  <!-- Descartar lead: substitui prompt() -->
  <Dialog
    ref="discardDialogRef"
    type="alert"
    width="md"
    :title="$t('PIPELINES.LEAD_DRAWER.DISCARD_DIALOG.TITLE')"
    :description="$t('PIPELINES.LEAD_DRAWER.DISCARD_DIALOG.DESCRIPTION')"
    :confirm-button-label="$t('PIPELINES.LEAD_DRAWER.DISCARD_DIALOG.CONFIRM')"
    :cancel-button-label="$t('PIPELINES.LEAD_DRAWER.DISCARD_DIALOG.CANCEL')"
    :disable-confirm-button="!discardReason.trim()"
    @confirm="confirmDiscard"
  >
    <div class="flex flex-col gap-2">
      <label class="text-body-small text-n-slate-11">{{ $t('PIPELINES.LEAD_DRAWER.DISCARD_DIALOG.REASON_LABEL') }}</label>
      <textarea
        v-model="discardReason"
        rows="3"
        :placeholder="$t('PIPELINES.LEAD_DRAWER.DISCARD_DIALOG.REASON_PLACEHOLDER')"
        class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-body-main text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
      />
    </div>
  </Dialog>

  <!-- Reabrir lead: substitui prompt() -->
  <Dialog
    ref="reopenDialogRef"
    width="md"
    :title="$t('PIPELINES.LEAD_DRAWER.REOPEN_DIALOG.TITLE')"
    :description="$t('PIPELINES.LEAD_DRAWER.REOPEN_DIALOG.DESCRIPTION')"
    :confirm-button-label="$t('PIPELINES.LEAD_DRAWER.REOPEN_DIALOG.CONFIRM')"
    :cancel-button-label="$t('PIPELINES.LEAD_DRAWER.REOPEN_DIALOG.CANCEL')"
    :disable-confirm-button="!reopenReason.trim()"
    @confirm="confirmReopen"
  >
    <div class="flex flex-col gap-2">
      <label class="text-body-small text-n-slate-11">{{ $t('PIPELINES.LEAD_DRAWER.REOPEN_DIALOG.REASON_LABEL') }}</label>
      <textarea
        v-model="reopenReason"
        rows="3"
        :placeholder="$t('PIPELINES.LEAD_DRAWER.REOPEN_DIALOG.REASON_PLACEHOLDER')"
        class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-body-main text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
      />
    </div>
  </Dialog>
</template>
